import Caret from '@clearkit/icons/glyphs/Caret';
import { CKButton, CKButtonProps, CKSelect } from 'clearkit';

import { CaptureProspects } from './CaptureProspects';
import { ErrorBoundary } from './ErrorBoundary';
import {
  FilterSelectItem,
  FilterValue,
  useProspectFilters,
} from './useProspectFilters';
import { useProspects } from './useProspects';

type CapturePreviewProps = {
  domain: string;
};

function CapturePreviewInternal({ domain }: CapturePreviewProps) {
  const {
    role,
    roleItems,
    seniority,
    seniorityItems,
    update,
    loading,
  } = useProspectFilters();
  const { data } = useProspects(domain, { role, seniority });

  const contactCount = data?.totalCount ?? 0;
  const contactLabel = contactCount === 1 ? 'contact' : 'contacts';

  const selectButtonProps = {
    className: 'lowercase',
    variant: 'tertiary',
    variantColor: 'blue',
    rightIcon: <Caret />,
  } as CKButtonProps;

  const selectedSeniority = getItem(seniorityItems, seniority);
  const selectedRole = getItem(roleItems, role);

  if (loading) {
    return null;
  }

  return (
    <div className="p-6 pb-0 mb-4">
      <header className="mb-6">
        <p className="mb-4 text-base">
          Clearbit Capture found {contactCount} {contactLabel} at this company
          based on the criteria below.{' '}
          <a
            className="font-semibold text-gray-800"
            href="https://clearbit.com/blog/clearbit-capture"
          >
            Learn more about Clearbit Capture
          </a>
          .
        </p>
        <div className="flex items-center text-sm gap-1">
          Showing
          <CKSelect
            disclosure={
              <CKButton {...selectButtonProps}>
                {getLabel(selectedSeniority) || 'seniority'}
              </CKButton>
            }
            items={seniorityItems}
            onSelectedItemChange={(changes: any) =>
              update('seniority', changes.selectedItem.value)
            }
            placeholder="seniority"
            popoverPlacement="bottom-start"
            popoverWidth="20ch"
            selectedItem={selectedSeniority}
          />
          level in
          <CKSelect
            disclosure={
              <CKButton {...selectButtonProps}>
                {getLabel(selectedRole) || 'role'}
              </CKButton>
            }
            items={roleItems}
            onSelectedItemChange={(changes: any) =>
              update('role', changes.selectedItem.value)
            }
            placeholder="role"
            popoverPlacement="bottom-start"
            popoverWidth="20ch"
            selectedItem={selectedRole}
          />
        </div>
      </header>
      <CaptureProspects prospects={data?.nodes} />
    </div>
  );
}

export function CapturePreview(props: CapturePreviewProps) {
  return (
    <ErrorBoundary>
      <CapturePreviewInternal {...props} />
    </ErrorBoundary>
  );
}

function getItem(items: FilterSelectItem[], query: string) {
  return items
    .filter((item): item is FilterValue => 'label' in item && !!item.label)
    .find((item) => item.value === query);
}

function getLabel(filter?: FilterValue) {
  if (!filter?.value) {
    return;
  }

  return filter.label;
}
